import {
  Alert,
  Button,
  DialogActions,
  DialogContent,
  DialogContentText,
  TextField,
} from "@mui/material";
import CustomDialog from "Components/CustomDialog/CustomDialog";
import ConnectButton from "features/dataConnectors/components/ConnectButton";
import { IWooCommerceConnectButtonProps } from "features/dataConnectors/types";
import { normalizeStoreUrl } from "features/dataConnectors/utils/normalizeStoreUrl";
import { useSelectedProject } from "Hooks";
import { useCallback, useEffect, useMemo, useState } from "react";

function WooCommerceConnectButton({
  oauthClientId,
  service,
  syncClients,
  setSyncClients,
  state,
  linkAccountProjectId,
  onConnected,
  shop: shopProp,
  skipStoreUrlPrompt,
  storeUrl: controlledStoreUrl,
  onStoreUrlChange,
  connectButtonProps,
  connectButtonLabel,
}: IWooCommerceConnectButtonProps) {
  const { selectedProject } = useSelectedProject();
  const [open, setOpen] = useState(false);
  const [internalStoreUrl, setInternalStoreUrl] = useState("");

  const defaultStoreUrl = useMemo(() => {
    const domain = (selectedProject?.domains || []).find(
      (d) => d.isPrimary && d.domain,
    )?.domain;
    return domain ? normalizeStoreUrl(domain) : "";
  }, [selectedProject?.domains]);

  const isControlled = controlledStoreUrl !== undefined;
  const storeUrl = isControlled ? controlledStoreUrl : internalStoreUrl;

  useEffect(() => {
    if (skipStoreUrlPrompt && !isControlled && !internalStoreUrl && defaultStoreUrl) {
      setInternalStoreUrl(defaultStoreUrl);
    }
  }, [skipStoreUrlPrompt, isControlled, internalStoreUrl, defaultStoreUrl]);

  const setStoreUrl = useCallback(
    (value: string) => {
      if (onStoreUrlChange) {
        onStoreUrlChange(value);
      } else {
        setInternalStoreUrl(value);
      }
    },
    [onStoreUrlChange],
  );

  const shop = useMemo(
    () => shopProp ?? normalizeStoreUrl(storeUrl),
    [shopProp, storeUrl],
  );

  const isDisabled = useMemo(() => {
    if (!shop) {
      return true;
    }
    try {
      return !new URL(shop).host;
    } catch {
      return true;
    }
  }, [shop]);

  const handleConnected = useCallback(
    (oauthClient) => {
      setOpen(false);
      if (!isControlled) {
        setInternalStoreUrl("");
      }
      if (onConnected) {
        onConnected(oauthClient);
      }
    },
    [onConnected, isControlled],
  );

  const handleOpen = useCallback(() => {
    setStoreUrl(defaultStoreUrl);
    setOpen(true);
  }, [defaultStoreUrl, setStoreUrl]);

  const handleClose = useCallback(() => {
    setOpen(false);
    if (!isControlled) {
      setInternalStoreUrl("");
    }
  }, [isControlled]);

  const connectAction = (
    <ConnectButton
      service={service}
      syncClients={syncClients}
      setSyncClients={setSyncClients}
      state={state}
      shop={shop}
      linkAccountProjectId={linkAccountProjectId}
      disabled={isDisabled}
      onConnected={handleConnected}
      oauthClientId={oauthClientId}
      connectButtonProps={connectButtonProps}
      connectButtonLabel={connectButtonLabel}
    />
  );

  if (skipStoreUrlPrompt) {
    return connectAction;
  }

  return (
    <>
      <Button size="small" variant="outlined" onClick={handleOpen}>
        {oauthClientId ? "Reconnect" : "Connect"}
      </Button>

      <CustomDialog open={open} fullWidth maxWidth="sm">
        <DialogContent>
          <DialogContentText mb={2}>
            Enter your WooCommerce store URL (e.g. https://example.com). You must be
            logged in as a store admin to approve the connection.
          </DialogContentText>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Connection will not work if your WordPress permalink structure is set to
            Plain (Settings → Permalinks). Use Post name or another pretty permalink
            option instead.
          </Alert>
          <TextField
            fullWidth
            label="Store URL"
            name="storeUrl"
            value={storeUrl}
            variant="outlined"
            onChange={(e) => setStoreUrl(e.target.value)}
            placeholder="https://your-store.com"
          />
        </DialogContent>
        <DialogActions>
          <Button color="secondary" onClick={handleClose}>
            Cancel
          </Button>
          <ConnectButton
            service={service}
            syncClients={syncClients}
            setSyncClients={setSyncClients}
            state={state}
            shop={shop}
            linkAccountProjectId={linkAccountProjectId}
            disabled={isDisabled}
            onConnected={handleConnected}
          />
        </DialogActions>
      </CustomDialog>
    </>
  );
}

export default WooCommerceConnectButton;
